import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Alert as MuiAlert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  Tabs, Tab, CircularProgress,
} from '@mui/material'
import RouterIcon from '@mui/icons-material/Router'
import SensorsIcon from '@mui/icons-material/Sensors'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import SettingsIcon from '@mui/icons-material/Settings'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CircleIcon from '@mui/icons-material/Circle'
import CodeIcon from '@mui/icons-material/Code'
import EditIcon from '@mui/icons-material/Edit'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { APIProvider, AdvancedMarker, Map } from '@vis.gl/react-google-maps'
import { useAuth } from '../contexts/AuthContext'
import { getGoogleMapsApiKey, getGoogleMapsMapId } from '../config/maps'
import {
  Gateway, GatewayDetail, DeviceRow, getGateways, getGateway, getDevices, getPacket,
  provisionGateway, assignGateway, unassignGateway, updateGateway, deleteGateway,
} from '../services/gateways'
import { getYards } from '../services/api'
import {
  BatteryCell, DeviceLogsDialog, EditDeviceDialog, TrendChip, formatDateTime, timeAgo, useMrtLocalization,
} from '../components/devices/deviceCommon'

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places']

interface YardLite { id: string; name: string; latitude?: number; longitude?: number }
type Toast = { msg: string; sev: 'success' | 'error' | 'info' }

function statusColor(g: Gateway): string {
  if (g.status === 'suspended') return '#9e9e9e'
  if (g.unassigned) return '#f6b400'
  if (!g.online) return '#ef5350'
  return '#43a047'
}

const DevicesScreen: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isSuperAdmin = (user as any)?.role === 'super_admin'
  const [tab, setTab] = useState(0)
  const [gateways, setGateways] = useState<Gateway[]>([])
  const [devices, setDevices] = useState<DeviceRow[]>([])
  const [yards, setYards] = useState<YardLite[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<Toast | null>(null)

  const [provisionOpen, setProvisionOpen] = useState(false)
  const [assignFor, setAssignFor] = useState<Gateway | null>(null)
  const [manageId, setManageId] = useState<string | null>(null)
  const [packetId, setPacketId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [gw, dev, yardsRes] = await Promise.all([getGateways(), getDevices(), getYards()])
      setGateways(gw)
      setDevices(dev)
      setYards((yardsRes.data || []).map((y: any) => ({ id: y.id || y._id, name: y.name, latitude: y.latitude, longitude: y.longitude })))
    } catch (e) {
      setToast({ msg: t('devices.toastLoadFailed'), sev: 'error' })
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  const stats = useMemo(() => ({
    total: gateways.length,
    online: gateways.filter(g => g.online).length,
    unassigned: gateways.filter(g => g.unassigned).length,
    activeDevices: gateways.reduce((s, g) => s + g.activeDeviceCount, 0),
  }), [gateways])

  const mapGateways = gateways.filter(g => g.gps)
  const mapCenter = mapGateways.length ? { lat: mapGateways[0].gps!.lat, lng: mapGateways[0].gps!.lng } : { lat: 32.0853, lng: 34.7818 }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <RouterIcon /> {t('devices.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>{t('devices.refresh')}</Button>
          {isSuperAdmin && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setProvisionOpen(true)}>{t('devices.addGateway')}</Button>}
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<RouterIcon />} iconPosition="start" label={`${t('devices.tabGateways')} (${gateways.length})`} />
        <Tab icon={<SensorsIcon />} iconPosition="start" label={`${t('devices.tabDevices')} (${devices.length})`} />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <StatCard label={t('devices.statGateways')} value={stats.total} />
            <StatCard label={t('devices.statOnline')} value={stats.online} color="#43a047" />
            <StatCard label={t('devices.statUnassigned')} value={stats.unassigned} color={stats.unassigned ? '#f6b400' : undefined} warn={stats.unassigned > 0} />
            <StatCard label={t('devices.statActiveDevices')} value={stats.activeDevices} color="#1976d2" />
          </Box>

          <Paper sx={{ height: 360, mb: 3, overflow: 'hidden', borderRadius: 2 }}>
            <APIProvider apiKey={getGoogleMapsApiKey()} libraries={GOOGLE_MAPS_LIBRARIES}>
              <Map mapId={getGoogleMapsMapId()} defaultCenter={mapCenter} defaultZoom={mapGateways.length ? 11 : 7} gestureHandling="greedy" disableDefaultUI={false} style={{ width: '100%', height: '100%' }}>
                {mapGateways.map(g => (
                  <AdvancedMarker key={g.id} position={{ lat: g.gps!.lat, lng: g.gps!.lng }} onClick={() => setManageId(g.id)}>
                    <div style={{ position: 'relative', width: 38, height: 38, borderRadius: '50%', background: statusColor(g), border: '3px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer' }}>
                      <RouterIcon sx={{ fontSize: 18 }} />
                      <div style={{ position: 'absolute', top: -8, right: -8, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 10, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>{g.activeDeviceCount}</div>
                      {g.unassigned && (
                        <div style={{ position: 'absolute', bottom: -8, left: -8, width: 18, height: 18, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <WarningAmberIcon sx={{ fontSize: 14, color: '#f6b400' }} />
                        </div>
                      )}
                    </div>
                  </AdvancedMarker>
                ))}
              </Map>
            </APIProvider>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('devices.colGateway')}</TableCell>
                  <TableCell>{t('devices.colStatus')}</TableCell>
                  <TableCell>{t('devices.colSite')}</TableCell>
                  <TableCell align="center">{t('devices.colDevices')}</TableCell>
                  <TableCell align="center">{t('devices.colBattery')}</TableCell>
                  <TableCell>{t('devices.colLastSeen')}</TableCell>
                  <TableCell align="right">{t('devices.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map(g => (
                  <TableRow key={g.id} hover>
                    <TableCell>
                      <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {g.name || g.macAddress}
                        {g.autoRegistered && (
                          <Chip size="small" variant="outlined" color="info" label={t('devices.autoRegistered')} />
                        )}
                      </Box>
                      <Box sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'monospace' }}>{g.macAddress}</Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" icon={<CircleIcon sx={{ fontSize: '12px !important', color: `${statusColor(g)} !important` }} />}
                        label={g.status === 'suspended' ? t('devices.statusSuspended') : g.online ? t('devices.statusOnline') : t('devices.statusOffline')} />
                    </TableCell>
                    <TableCell>
                      {g.yardName ? (
                        <Chip size="small" label={g.yardName} variant="outlined" icon={g.assignmentMode === 'auto' ? <LinkIcon sx={{ fontSize: '14px !important' }} /> : undefined} />
                      ) : (
                        <Button size="small" color="warning" startIcon={<WarningAmberIcon />} onClick={() => setAssignFor(g)}>{t('devices.assign')}</Button>
                      )}
                    </TableCell>
                    <TableCell align="center">{g.activeDeviceCount}/{g.deviceCount}</TableCell>
                    <TableCell align="center">{g.batteryPercent != null ? `${g.batteryPercent}%` : '—'}</TableCell>
                    <TableCell>{timeAgo(g.lastSeenAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('devices.manage')}><IconButton size="small" onClick={() => setManageId(g.id)}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
                      {g.yardId ? (
                        <Tooltip title={t('devices.unassign')}><IconButton size="small" onClick={async () => { await unassignGateway(g.id); setToast({ msg: t('devices.toastUnassigned'), sev: 'info' }); load() }}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                      ) : (
                        <Tooltip title={t('devices.assignToSite')}><IconButton size="small" color="warning" onClick={() => setAssignFor(g)}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && gateways.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('devices.empty')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 1 && (
        <DevicesTable devices={devices} loading={loading} onChanged={load} notify={setToast} />
      )}

      <ProvisionDialog open={provisionOpen} onClose={() => setProvisionOpen(false)} onDone={() => { setProvisionOpen(false); load() }} notify={setToast} />
      <AssignDialog gateway={assignFor} yards={yards} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load() }} notify={setToast} />
      <ManageDialog id={manageId} yards={yards} onClose={() => setManageId(null)} onChanged={load} notify={setToast} onViewPacket={setPacketId} />
      <PacketDialog packetId={packetId} onClose={() => setPacketId(null)} />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <MuiAlert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</MuiAlert> : undefined}
      </Snackbar>
    </Box>
  )
}

const DevicesTable: React.FC<{
  devices: DeviceRow[]
  loading: boolean
  onChanged: () => void
  notify: (t: Toast) => void
}> = ({ devices, loading, onChanged, notify }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const localization = useMrtLocalization()
  const [editFor, setEditFor] = useState<DeviceRow | null>(null)
  const [logsFor, setLogsFor] = useState<DeviceRow | null>(null)

  const columns = useMemo<MRT_ColumnDef<DeviceRow>[]>(() => [
    {
      accessorKey: 'name',
      header: t('devices.colName'),
      size: 180,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircleIcon sx={{ fontSize: 10, color: row.original.online ? '#43a047' : '#9e9e9e' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.original.name || <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>—</Box>}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'macAddress',
      header: t('devices.colSerial'),
      size: 190,
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{cell.getValue<string>()}</Typography>
      ),
    },
    {
      accessorKey: 'gatewayName',
      header: t('devices.colGateway'),
      size: 170,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2">{row.original.gatewayName || '—'}</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {row.original.gatewayMacAddress}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'yardName',
      header: t('devices.colSite'),
      size: 150,
      Cell: ({ row }) => row.original.yardName
        ? <Chip size="small" label={row.original.yardName} variant="outlined" />
        : <Chip size="small" color="warning" icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />} label={t('devices.unassigned')} />,
    },
    {
      accessorKey: 'lastBatteryPercent',
      header: t('devices.colBattery'),
      size: 150,
      Cell: ({ cell }) => <BatteryCell pct={cell.getValue<number | null>()} />,
    },
    {
      accessorKey: 'lastSeenAt',
      header: t('devices.colLastTransmission'),
      size: 190,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2">{formatDateTime(row.original.lastSeenAt)}</Typography>
          <Typography variant="caption" color="text.secondary">{timeAgo(row.original.lastSeenAt)}</Typography>
        </Box>
      ),
    },
  ], [t])

  const table = useMaterialReactTable({
    columns,
    data: devices,
    localization,
    enableColumnActions: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    displayColumnDefOptions: { 'mrt-row-actions': { header: t('devices.colActions'), size: 110 } },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title={t('devices.logs')}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setLogsFor(row.original) }}>
            <ReceiptLongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('devices.edit')}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setEditFor(row.original) }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(`/devices/${encodeURIComponent(row.original.macAddress)}`),
      sx: { cursor: 'pointer' },
    }),
    state: { isLoading: loading },
    initialState: { density: 'comfortable', pagination: { pageIndex: 0, pageSize: 25 } },
    getRowId: r => r.macAddress,
    muiTablePaperProps: { sx: { borderRadius: 3 } },
    renderEmptyRowsFallback: () => (
      <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('devices.noDevicesList')}</Typography>
    ),
  })

  return (
    <>
      <MaterialReactTable table={table} />
      <EditDeviceDialog
        device={editFor}
        onClose={() => setEditFor(null)}
        onSaved={() => { setEditFor(null); onChanged() }}
        notify={(msg, sev) => notify({ msg, sev })}
      />
      <DeviceLogsDialog device={logsFor} onClose={() => setLogsFor(null)} />
    </>
  )
}

const StatCard: React.FC<{ label: string; value: number; color?: string; warn?: boolean }> = ({ label, value, color, warn }) => (
  <Paper sx={{ p: 2, minWidth: 140, borderLeft: `4px solid ${color || '#bdbdbd'}` }}>
    <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
      {warn && <WarningAmberIcon color="warning" />}{value}
    </Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
)

const ProvisionDialog: React.FC<{ open: boolean; onClose: () => void; onDone: () => void; notify: (t: Toast) => void }> = ({ open, onClose, onDone, notify }) => {
  const { t } = useTranslation()
  const [mac, setMac] = useState('')
  const [name, setName] = useState('')
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const reset = () => { setMac(''); setName(''); setApiKey(null) }
  const submit = async () => {
    setSaving(true)
    try {
      const res = await provisionGateway({ macAddress: mac, name: name || undefined })
      setApiKey(res.apiKey)
      notify({ msg: t('devices.toastProvisioned'), sev: 'success' })
    } catch (e: any) {
      notify({ msg: e?.response?.data?.error || t('devices.toastProvisionFailed'), sev: 'error' })
    } finally { setSaving(false) }
  }
  return (
    <Dialog open={open} onClose={() => { reset(); onClose() }} fullWidth maxWidth="sm">
      <DialogTitle>{t('devices.provisionTitle')}</DialogTitle>
      <DialogContent>
        {apiKey ? (
          <Box sx={{ mt: 1 }}>
            <MuiAlert severity="success" sx={{ mb: 2 }}>{t('devices.keyCreatedNote')}</MuiAlert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {apiKey}
              <IconButton size="small" aria-label={t('devices.copy')} onClick={() => { navigator.clipboard.writeText(apiKey); notify({ msg: t('devices.copied'), sev: 'info' }) }}><ContentCopyIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('devices.macLabel')} value={mac} onChange={e => setMac(e.target.value)} placeholder="AABBCC112233" fullWidth />
            <TextField label={t('devices.nameOptional')} value={name} onChange={e => setName(e.target.value)} fullWidth />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {apiKey ? (
          <Button variant="contained" onClick={() => { reset(); onDone() }}>{t('devices.done')}</Button>
        ) : (
          <>
            <Button onClick={() => { reset(); onClose() }}>{t('devices.cancel')}</Button>
            <Button variant="contained" disabled={saving || mac.replace(/[:\-\s]/g, '').length !== 12} onClick={submit}>{t('devices.provision')}</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const AssignDialog: React.FC<{ gateway: Gateway | null; yards: YardLite[]; onClose: () => void; onDone: () => void; notify: (t: Toast) => void }> = ({ gateway, yards, onClose, onDone, notify }) => {
  const { t } = useTranslation()
  const [yardId, setYardId] = useState('')
  useEffect(() => { setYardId(gateway?.yardId || '') }, [gateway])
  if (!gateway) return null
  return (
    <Dialog open={!!gateway} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{(gateway.name || gateway.macAddress)} — {t('devices.assignToASite')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>{t('devices.site')}</InputLabel>
          <Select label={t('devices.site')} value={yardId} onChange={e => setYardId(e.target.value)}>
            {yards.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('devices.cancel')}</Button>
        <Button variant="contained" disabled={!yardId} onClick={async () => {
          try { await assignGateway(gateway.id, yardId); notify({ msg: t('devices.toastAssigned'), sev: 'success' }); onDone() }
          catch { notify({ msg: t('devices.toastAssignFailed'), sev: 'error' }) }
        }}>{t('devices.assign')}</Button>
      </DialogActions>
    </Dialog>
  )
}

const ManageDialog: React.FC<{ id: string | null; yards: YardLite[]; onClose: () => void; onChanged: () => void; notify: (t: Toast) => void; onViewPacket: (packetId: string) => void }> = ({ id, yards, onClose, onChanged, notify, onViewPacket }) => {
  const { t } = useTranslation()
  const [detail, setDetail] = useState<GatewayDetail | null>(null)
  const [name, setName] = useState('')

  useEffect(() => {
    if (!id) { setDetail(null); return }
    getGateway(id).then(d => { setDetail(d); setName(d.name || '') })
      .catch(() => notify({ msg: t('devices.toastLoadFailed'), sev: 'error' }))
  }, [id, notify, t])

  if (!id || !detail) return null

  const save = async () => {
    try { await updateGateway(id, { name }); notify({ msg: t('devices.toastSaved'), sev: 'success' }); onChanged() }
    catch { notify({ msg: t('devices.toastSaveFailed'), sev: 'error' }) }
  }
  const toggleStatus = async () => {
    const next = detail.status === 'active' ? 'suspended' : 'active'
    try { await updateGateway(id, { status: next }); setDetail({ ...detail, status: next }); onChanged() }
    catch { notify({ msg: t('devices.toastSaveFailed'), sev: 'error' }) }
  }

  return (
    <Dialog open={!!id} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RouterIcon /> {detail.name || detail.macAddress}
        <Chip size="small" sx={{ ml: 1 }} label={detail.status === 'suspended' ? t('devices.statusSuspended') : detail.status === 'active' ? t('devices.statusActive') : detail.status} color={detail.status === 'active' ? 'success' : 'default'} />
        {detail.unassigned && <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={t('devices.unassigned')} />}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
          <InfoLine label={t('devices.mac')} value={detail.macAddress} mono />
          <InfoLine label={t('devices.site')} value={detail.yardName || t('devices.unassigned')} />
          <InfoLine label={t('devices.online')} value={detail.online ? t('devices.statusOnline') : t('devices.statusOffline')} />
          <InfoLine label={t('devices.battery')} value={detail.batteryPercent != null ? `${detail.batteryPercent}%` : '—'} />
          <InfoLine label={t('devices.version')} value={detail.gatewayVersion || '—'} />
          <InfoLine label={t('devices.devicesLabel')} value={`${detail.activeDeviceCount}/${detail.deviceCount}`} />
          {detail.apiKeyPrefix && <InfoLine label={t('devices.apiKey')} value={`${detail.apiKeyPrefix}…`} mono />}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label={t('devices.name')} value={name} onChange={e => setName(e.target.value)} />
          <Button variant="contained" onClick={save}>{t('devices.save')}</Button>
          <Button color={detail.status === 'active' ? 'warning' : 'success'} onClick={toggleStatus}>
            {detail.status === 'active' ? t('devices.suspend') : t('devices.activate')}
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('devices.connectedDevices')} ({detail.sensors.length})</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 200 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>{t('devices.sensor')}</TableCell><TableCell>{t('devices.trend')}</TableCell><TableCell>{t('devices.battery')}</TableCell><TableCell>{t('devices.signal')}</TableCell><TableCell>{t('devices.colLastSeen')}</TableCell></TableRow></TableHead>
            <TableBody>
              {detail.sensors.map(s => (
                <TableRow key={s.macAddress}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.macAddress}</TableCell>
                  <TableCell><TrendChip trend={s.lastTrend} /></TableCell>
                  <TableCell>{s.lastBatteryPercent != null ? `${s.lastBatteryPercent}%` : '—'}</TableCell>
                  <TableCell>{s.lastSignal ?? '—'}</TableCell>
                  <TableCell>{timeAgo(s.lastSeenAt)}</TableCell>
                </TableRow>
              ))}
              {detail.sensors.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('devices.noDevices')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>

        {detail.recentAlerts.length > 0 && (
          <>
            <Typography variant="subtitle2">{t('devices.recentAlerts')}</Typography>
            <Box sx={{ mb: 2 }}>
              {detail.recentAlerts.map(a => (
                <MuiAlert key={a.id} severity={a.type === 'error' ? 'error' : a.type === 'warning' ? 'warning' : 'info'} sx={{ mb: 0.5 }}>
                  {a.message} <span style={{ opacity: 0.7 }}>· {formatDateTime(a.date)}</span>
                </MuiAlert>
              ))}
            </Box>
          </>
        )}

        <Typography variant="subtitle2">{t('devices.telemetryLog')} ({detail.recentPackets.length})</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>{t('devices.received')}</TableCell><TableCell>{t('devices.packet')}</TableCell><TableCell align="center">{t('devices.sensors')}</TableCell><TableCell align="center">{t('devices.battery')}</TableCell><TableCell align="right">{t('devices.viewJson')}</TableCell></TableRow></TableHead>
            <TableBody>
              {detail.recentPackets.map(p => (
                <TableRow key={p.packetId} hover sx={{ cursor: 'pointer' }} onClick={() => onViewPacket(p.packetId)}>
                  <TableCell>{formatDateTime(p.receivedAt)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.packetId}</TableCell>
                  <TableCell align="center">{p.sensorCount}</TableCell>
                  <TableCell align="center">{p.batteryPercent}%</TableCell>
                  <TableCell align="right"><IconButton size="small" aria-label={t('devices.viewJson')}><CodeIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {detail.recentPackets.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('devices.noTelemetry')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 2 }} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={async () => {
          if (!window.confirm(t('devices.confirmDelete'))) return
          try { await deleteGateway(id); notify({ msg: t('devices.toastDeleted'), sev: 'info' }); onChanged(); onClose() }
          catch { notify({ msg: t('devices.toastDeleteFailed'), sev: 'error' }) }
        }}>{t('devices.delete')}</Button>
        <Box>
          {detail.unassigned ? (
            <AssignInline detail={detail} yards={yards} onDone={() => { onChanged(); getGateway(id).then(setDetail) }} notify={notify} />
          ) : (
            <Button startIcon={<LinkOffIcon />} onClick={async () => { await unassignGateway(id); notify({ msg: t('devices.toastUnassigned'), sev: 'info' }); onChanged(); getGateway(id).then(setDetail) }}>{t('devices.unassign')}</Button>
          )}
          <Button onClick={onClose} sx={{ ml: 1 }}>{t('devices.close')}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const PacketDialog: React.FC<{ packetId: string | null; onClose: () => void }> = ({ packetId, onClose }) => {
  const { t } = useTranslation()
  const [json, setJson] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!packetId) { setJson(null); return }
    setLoading(true)
    getPacket(packetId).then(p => setJson(JSON.stringify(p.received, null, 2))).catch(() => setJson(JSON.stringify({ error: t('devices.packetLoadFailed') }, null, 2))).finally(() => setLoading(false))
  }, [packetId, t])
  if (!packetId) return null
  return (
    <Dialog open={!!packetId} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CodeIcon /> {t('devices.packetTitle')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary">{t('devices.receivedJson')} · {packetId}</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box component="pre" sx={{ mt: 1, p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 420, fontFamily: 'monospace' }}>
            {json}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {json && <Button startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard.writeText(json)}>{t('devices.copy')}</Button>}
        <Button onClick={onClose}>{t('devices.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

const AssignInline: React.FC<{ detail: GatewayDetail; yards: YardLite[]; onDone: () => void; notify: (t: Toast) => void }> = ({ detail, yards, onDone, notify }) => {
  const { t } = useTranslation()
  const [yardId, setYardId] = useState('')
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>{t('devices.assignToSite')}</InputLabel>
        <Select label={t('devices.assignToSite')} value={yardId} onChange={e => setYardId(e.target.value)}>
          {yards.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
        </Select>
      </FormControl>
      <Button variant="contained" disabled={!yardId} startIcon={<LinkIcon />} onClick={async () => {
        try { await assignGateway(detail.id, yardId); notify({ msg: t('devices.toastAssigned'), sev: 'success' }); onDone() }
        catch { notify({ msg: t('devices.toastAssignFailed'), sev: 'error' }) }
      }}>{t('devices.assign')}</Button>
    </Box>
  )
}

const InfoLine: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>{value}</Typography>
  </Box>
)

export default DevicesScreen
